import { Activity } from './interfaces';

import { safeStripHtml } from '../../../../../Shared';

const RESULTS_PER_PAGE = 20

export const AVERAGE_FONT_WIDTH = 7

export const FILL_IN_THE_BLANK = 'Fill in the Blank'
export const WRITING_SENTENCES = 'Writing Sentences'

export const LEARN_TO_WRITE = 'Learn to Write'
export const WRITE_TO_LEARN = 'Write to Learn'
export const OTHER_FOCUS_GROUP_NAME = 'Other'

export const FOCUS_GROUP_DESCRIPTORS = {
  [LEARN_TO_WRITE]: 'Language Skills',
  [WRITE_TO_LEARN]: 'Subject Knowledge',
}

export function focusGroupOrder(name: string): number {
  if (name === LEARN_TO_WRITE) { return 0 }
  if (name === WRITE_TO_LEARN) { return 1 }
  return 2
}

export const calculateNumberOfPages = (activities: Activity[]) => Math.ceil(activities.length / RESULTS_PER_PAGE)

export const lowerBound = (currentPage: number): number => (currentPage - 1) * RESULTS_PER_PAGE;

export const upperBound = (currentPage: number): number => currentPage * RESULTS_PER_PAGE;

export const activityClassificationGroupings = [
  {
    group: 'Independent: Reading Texts',
    keys: ['evidence'],
    new: true
  },
  {
    group: 'Independent: Language Skills',
    keys: ['connect', 'sentence', 'passage']
  },
  {
    group: 'Independent: Proofreading',
    keys: ['passage']
  },
  {
    group: 'Whole Class Instruction',
    keys: ['lessons']
  },
  {
    group: 'Diagnostics',
    keys: ['diagnostic']
  }
]

export const getNumberFromString = (string) => {
  if (!string) { return null }

  const numberMatch = string.match(/\d+/g)
  if (numberMatch) { return Number(numberMatch[0]) }

  return null
}

export const ACTIVITY_CLASSIFICATION_FILTERS = 'activityClassificationFilters'

export const ACTIVITY_FOCUS_FILTERS = 'activityFocusFilters'

export const CONTENT_PARTNER_FILTERS = 'contentPartnerFilters'

export const TYPING_REQUIREMENT_FILTERS = 'typingRequirementFilters'

export const EARLY_ACCESS_FILTERS = 'earlyAccessFilters'

export const FLAG_FILTERS = 'flagFilters'

export const TOPIC_FILTERS = 'topicFilters'

export const SAVED_ACTIVITY_FILTERS = 'savedActivityFilters'

export const STANDARDS_FILTERS = 'standardsFilters'

export const TARGET_GRADE_RANGE_FILTERS = 'targetGradeRangeFilters'

export const TARGET_GRADE_RANGE_BANDS = ['3-5', '6-8', '8-12']

// The "Additional Activities" callout only surfaces lower-band activities when
// the filtered result set is smaller than this; at/above it we assume the teacher
// already has plenty of on-band activities to choose from.
export const ADDITIONAL_ACTIVITIES_THRESHOLD = 50

// The bands ordered below the lowest currently-selected band (e.g. selecting
// "8-12" yields ["3-5", "6-8"]). Empty when nothing is selected or the lowest
// band ("3-5") is already selected.
export function lowerBands(targetGradeRangeFilters: string[]): string[] {
  const selectedIndexes = targetGradeRangeFilters.map(band => TARGET_GRADE_RANGE_BANDS.indexOf(band)).filter(index => index >= 0)
  if (!selectedIndexes.length) { return [] }

  return TARGET_GRADE_RANGE_BANDS.slice(0, Math.min(...selectedIndexes))
}

// An activity's floor band is the lowest band it's assigned to -- the darker-green
// "target grade" chip. Bands above the floor are "also suitable for higher grades".
export function floorBand(gradeBandNames: string[]): string | null {
  const indexes = (gradeBandNames || []).map(band => TARGET_GRADE_RANGE_BANDS.indexOf(band)).filter(index => index >= 0)
  if (!indexes.length) { return null }

  return TARGET_GRADE_RANGE_BANDS[Math.min(...indexes)]
}

export function arrayFromNumbers(lowerValue: number, upperValue: number) {
  const array = []
  for (let i = lowerValue; i <= upperValue; i++) {
    array.push(i)
  }
  return array
}

// Building an activity's searchable string means stringifying the whole object
// and running it through an HTML parser -- ~41ms for a full pass over the
// library. The filter pipeline runs ~12x per keystroke (the result list plus
// every filter section's counts), so recomputing it made each keystroke cost
// ~500ms of blocked main thread. Compute it once per activity object instead;
// WeakMap entries fall away with the objects when the list is refetched.
const searchStringCache = new WeakMap<Activity, string>()

function searchStringForActivity(activity: Activity) {
  let searchString = searchStringCache.get(activity)
  if (searchString === undefined) {
    searchString = safeStripHtml(JSON.stringify(Object.values(activity)).toLowerCase().replace(/\\"/g, '"'))
    searchStringCache.set(activity, searchString)
  }
  return searchString
}

function filterBySearch(search: string, activity: Activity) {
  if (!search.length) { return true }

  return searchStringForActivity(activity).includes(search.toLowerCase())
}

function filterByActivityClassification(activityClassificationFilters: string[], activity: Activity) {
  if (!activityClassificationFilters.length) { return true }
  return activityClassificationFilters.includes(activity.activity_classification.key)
}

function filterByActivityFocus(activityFocusFilters: number[], activity: Activity) {
  if (!activityFocusFilters.length) { return true }
  return activityFocusFilters.includes(activity.activity_focus_id)
}

function filterByStandards(standardsFilters: { ellFilters: string[]}, activity: Activity) {
  const { ellFilters, } = standardsFilters

  if (!ellFilters.length) { return true }

  return filterByELL(ellFilters, activity)
}

function filterByELL(ellLevelFilters: string[], activity: Activity) {
  if (!activity.standard_level_name?.includes('ELL')) { return }

  return ellLevelFilters.includes(activity.standard_level_name)
}

function filterByTargetGradeRange(targetGradeRangeFilters: string[], activity: Activity) {
  if (!targetGradeRangeFilters.length) { return true }

  return (activity.grade_band_names || []).some(band => targetGradeRangeFilters.includes(band))
}

function filterByContentPartners(contentPartnerFilters: number[], activity: Activity) {
  if (!contentPartnerFilters.length) { return true }
  return contentPartnerFilters.some(id => activity.content_partners.some(cp => cp.id === id))
}

function filterByEarlyAccess(earlyAccessFilters: string[], activity: Activity) {
  if (!earlyAccessFilters.length) { return true }
  return earlyAccessFilters.some(flag => activity.flags.includes(flag))
}

function filterByTypingRequirements(typingRequirementFilters: string[], activity: Activity) {
  if (!typingRequirementFilters.length) { return true }

  if (typingRequirementFilters.includes(FILL_IN_THE_BLANK) && activity.fill_in_the_blank) { return true }
  if (typingRequirementFilters.includes(WRITING_SENTENCES) && !activity.fill_in_the_blank) { return true }

  return false
}

function filterByTopic(topicFilters: number[], activity: Activity) {
  if (!topicFilters.length) { return true }
  return topicFilters.some(id => activity.topics.some(cp => cp.id === id))
}

function filterBySavedActivityIds(savedActivityFilters: number[], activity: Activity) {
  if (!savedActivityFilters.length) { return true }
  return savedActivityFilters.some(id => Number(activity.id) === Number(id))
}

export function filterByFlag(flagFilters: string[], activity:Activity) {
  if (!flagFilters.length) { return true }
  return flagFilters.some(flag => activity.flags.includes(flag))
}

export const filters = {
  search: filterBySearch,
  [ACTIVITY_CLASSIFICATION_FILTERS]: filterByActivityClassification,
  [STANDARDS_FILTERS]: filterByStandards,
  [TARGET_GRADE_RANGE_FILTERS]: filterByTargetGradeRange,
  [ACTIVITY_FOCUS_FILTERS]: filterByActivityFocus,
  [CONTENT_PARTNER_FILTERS]: filterByContentPartners,
  [EARLY_ACCESS_FILTERS]: filterByEarlyAccess,
  [TOPIC_FILTERS]: filterByTopic,
  [SAVED_ACTIVITY_FILTERS]: filterBySavedActivityIds,
  [FLAG_FILTERS]: filterByFlag,
  [TYPING_REQUIREMENT_FILTERS]: filterByTypingRequirements,
}

export const stringifyLowerLevelTopics = (topics) => {
  return topics.map(topic => topic.name).join()
}

const activityFocusSort = (activities) => activities.sort((a, b) => {
  if (!a.activity_focus_name) { return 1 }
  if (!b.activity_focus_name) { return -1 }
  return a.activity_focus_name.localeCompare(b.activity_focus_name)
})

const topicSort = (activities) => {
  const sortedActivities = activities.sort((a, b) => {
    if (!(a.topics && a.topics.length)) { return 1 }
    if (!(b.topics && b.topics.length)) { return -1 }
    return stringifyLowerLevelTopics(a.topics).localeCompare(stringifyLowerLevelTopics(b.topics))
  })
  return sortedActivities
}

export const DIAGNOSTIC_ORDER = [
  'f964c13c-d7b5-41cc-90b6-2ede42d0c6f6', // Starter (Pre)
  'c839ad1c-bade-41fc-a836-06bc3314bad5', // Starter (Post)
  '0561132f-4048-4304-b394-0f6019021ff1', // Intermediate (Pre)
  '9bc4ea08-47d7-459c-b795-1e8c25b53849', // Intermediate (Post)
  'e43af6b5-069f-4371-9766-c7cec29874e6', // Advanced (Pre)
  '56963da8-48aa-4382-b672-f5a660f580fe', // Advanced (Post)
  '873840f0-2ecf-4dcb-bb5b-9acfe3c0b969', // ELL Starter (Pre)
  '7e7d7149-fe90-49c3-b6ae-7fe258dd6d0f', // ELL Starter (Post)
  'c0fd87d8-c3a2-4f03-abac-005649492dae', // ELL Intermediate (Pre)
  '4aadf4f9-2b89-4ede-8296-0c3c99d2dac8', // ELL Intermediate (Post)
  'a8444ff4-f971-447e-9495-f5f3c9f494b8', // ELL Advanced (Pre)
  '8eacbcfc-16f4-4974-a54e-22ca3cc473c7', // ELL Advanced (Post)
  '5dfe46da-e13c-4b5b-a71d-55a9ae5669b0', // SpringBoard
  '-M2nZy22Ey5g75LVsH25', // Pre-AP 1
  '-M2ngd3CX44IwmKr_PY7', // Pre-AP 2
  '-L_wPCxbrT6toCb1fnYR', // AP
]

const diagnosticSortIndex = (activity: Activity) => {
  const index = DIAGNOSTIC_ORDER.indexOf(activity.uid)
  return index === -1 ? DIAGNOSTIC_ORDER.length : index
}

// there are only a few target grade range values, so within each of them we fall back
// to the canonical diagnostic order that used to be the library's default sort
const targetGradeRangeSort = (activities: Activity[], descending?: boolean) => activities.sort((a, b) => {
  const floorA = getNumberFromString(a.grade_band_range)
  const floorB = getNumberFromString(b.grade_band_range)

  if (floorA && !floorB) { return -1 }
  if (!floorA && floorB) { return 1 }
  if (floorA && floorB && floorA !== floorB) { return descending ? floorB - floorA : floorA - floorB }

  return diagnosticSortIndex(a) - diagnosticSortIndex(b)
})

export const TARGET_GRADE_RANGE_ASCENDING = 'target-grade-range-asc'
const TARGET_GRADE_RANGE_DESCENDING = 'target-grade-range-desc'
const ACTIVITY_FOCUS = 'activity-focus'
const TOPIC = 'topic'

export const sortFunctions = {
  [TARGET_GRADE_RANGE_ASCENDING]: (activities) => targetGradeRangeSort(activities),
  [TARGET_GRADE_RANGE_DESCENDING]: (activities) => targetGradeRangeSort(activities, true),
  [ACTIVITY_FOCUS]: activityFocusSort,
  [TOPIC]: topicSort
}

export const sortOptions = [
  {
    label: 'Target Grade Range (Low to High)',
    key: TARGET_GRADE_RANGE_ASCENDING,
    value: TARGET_GRADE_RANGE_ASCENDING
  },
  {
    label: 'Target Grade Range (High to Low)',
    key: TARGET_GRADE_RANGE_DESCENDING,
    value: TARGET_GRADE_RANGE_DESCENDING
  },
  {
    label: 'Activity Focus',
    key: ACTIVITY_FOCUS,
    value: ACTIVITY_FOCUS
  },
  {
    label: 'Topic',
    key: TOPIC,
    value: TOPIC
  }
]
